import {Directive, ElementRef, HostListener, Input, NgModule, Renderer2, ContentChild, Output, EventEmitter, OnChanges, SimpleChanges} from '@angular/core';
import {A11yModule, ConfigurableFocusTrapFactory, FocusTrap} from '@angular/cdk/a11y';

@Directive({
    selector: '[componentLink]',
    standalone: false
})
export class ComponentLinkDirective {

    @Input() componentLink: string;

    constructor(private el: ElementRef) {}
}

@Directive({
    selector: '[componentContent]',
    standalone: false
})
export class ComponentContentDirective {

    @Input() componentContent: string;

    constructor(private el: ElementRef, private renderer: Renderer2) {
        renderer.setStyle(el.nativeElement, 'display', 'none');
    }
}

@Directive({
    selector: '[closeComponent]',
    standalone: false
})
export class CloseComponentDirective {

    @Input() closeComponent: string;

    constructor(private el: ElementRef) {}
}


@Directive({
    selector: '[wrapperComponent]',
    standalone: false
})
export class WrapperComponentDirective implements OnChanges {

    @Input() wrapperComponent: string;
    @Input() active: boolean;
    @Input() onlyOpen: boolean = false;
    @Input() openFromOutsideIds = [];
    @Input() withFocusTrap: boolean = false;
    @Input() closeOnOutsideClick: boolean = true;
    @Output() activeChange = new EventEmitter();
    @ContentChild(ComponentLinkDirective, {static: true}) link;
    @ContentChild(ComponentContentDirective, {static: true}) component;
    @ContentChild(CloseComponentDirective, {static: true}) closeIcon;
    isOpen = false;
    focusTrap: FocusTrap;
    private previouslyFocusedElement: HTMLElement | null = null;


    constructor(private el: ElementRef, private renderer: Renderer2, private focusTrapFactory: ConfigurableFocusTrapFactory) {}

    open() {
        this.isOpen = true;
        this.renderer.setStyle(this.component.el.nativeElement, 'display', 'block');
        this.activeChange.emit(true);
        if(this.withFocusTrap){
            const focusTrapRegionEl = this.component.el.nativeElement;
            this.previouslyFocusedElement = document.activeElement as HTMLElement;
            this.focusTrap = this.focusTrapFactory.create(focusTrapRegionEl);
            this.focusTrap.focusInitialElementWhenReady().then(hasMovedFocus => {
                // If there were no focusable elements, focus the focusTrapRegionEl itself so the keyboard navigation
                // still works. We need to check that `focus` is a function due to Universal.
                if (!hasMovedFocus && typeof this.component.nativeElement.focus === 'function') {
                    focusTrapRegionEl.focus();
                }
            });
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['active'] && changes['active'].currentValue &&  !this.isOpen) {
            this.open();
        }
        if (changes['active'] && !changes['active'].currentValue && this.isOpen) {
            this.close();
        }
    }

    close() {
        this.isOpen = false;
        this.renderer.setStyle(this.component.el.nativeElement, 'display', 'none');
        this.activeChange.emit(false);
        if(this.focusTrap){
            this.focusTrap.destroy();
        }
        if (this.previouslyFocusedElement) {
            this.previouslyFocusedElement.focus();
            this.previouslyFocusedElement = null;
        }
    }

    toggle() {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }


    @HostListener('click',  ['$event']) onClink(event: any) {
        if (event.target.attributes.componentlink && !this.isOpen) {
            this.open();
        } else if (event.target.attributes.closeComponent || event.target.attributes.componentlink && this.isOpen && !this.onlyOpen) {
            this.close();
        }
    }

    @HostListener('document:click',  ['$event']) onClinkOutside(event: any) {
        if (!this.el.nativeElement.contains(event.target) && this.openFromOutsideIds.indexOf(event.target.id) === -1 && this.closeOnOutsideClick) {
            this.close();
        }
        if (this.openFromOutsideIds.indexOf(event.target.id) > -1) {
            this.toggle();
        }
    }

    @HostListener('document:keydown', ['$event']) onKeydownHandler(event: KeyboardEvent) {
        if (event.key === "Escape") {
            this.close();
        }
    }
}


@NgModule({
    imports: [A11yModule],
    declarations: [WrapperComponentDirective, ComponentLinkDirective, ComponentContentDirective, CloseComponentDirective],
    exports: [WrapperComponentDirective, ComponentLinkDirective, ComponentContentDirective, CloseComponentDirective]
})
export class OpenComponentLinkModule {}
